import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Badge, Divider, Menu, Space, Layout, Modal, Input, Select } from 'antd';
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';
import { addMenu, clearMenu, setMenu } from 'redux/slices/menu';
import { clearUser } from 'redux/slices/auth';
import { setCurrentChat } from 'redux/slices/chat';
import { data as allRoutes } from 'configs/menu-config';
import { groupMenuBySection } from 'configs/menu-sections';
import useDidUpdate from 'helpers/useDidUpdate';
import useAdminPermissions from 'helpers/useAdminPermissions';
import { clearBags } from 'redux/slices/cart';
import { useTranslation } from 'react-i18next';
import getSystemIcons from 'helpers/getSystemIcons';
import NotificationBar from './notificationBar';
import AdminChatWatch, { ADMIN_CHAT_COUNTS_EVENT } from './admin-chat-watch';
import AdminOrderWatch from './admin-order-watch';
import AdminTitleBadge from './admin-title-badge';
import { directionChange, navCollapseTrigger } from 'redux/slices/theme';
import ThemeConfigurator from './theme-configurator';
import Scrollbars from 'react-custom-scrollbars';
import NavProfile from './nav-profile';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'EN' },
  { value: 'fr', label: 'FR' },
  { value: 'it', label: 'IT' },
  { value: 'de', label: 'DE' },
];
const { Sider } = Layout;

function normalizeMenuPath(pathname) {
  return pathname.replace(/^\//, '').split('?')[0];
}

function menuUrlMatches(url, path) {
  if (!url) return false;
  const segment = String(url).replace(/^\//, '');
  return path === segment || path.startsWith(`${segment}/`);
}

function findMenuSelection(menuList, path) {
  let selectedKey = null;
  let bestUrlLen = -1;
  const openKeys = new Set();

  const consider = (item, key, ancestorKeys) => {
    if (!item?.url || !menuUrlMatches(item.url, path)) return;
    const len = String(item.url).length;
    if (len <= bestUrlLen) return;
    bestUrlLen = len;
    selectedKey = key;
    openKeys.clear();
    ancestorKeys.forEach((k) => openKeys.add(k));
  };

  menuList?.forEach((item, idx) => {
    const parentKey = `${item.id}_${idx}`;
    consider(item, parentKey, []);

    item.submenu?.forEach((submenu, idy) => {
      const subKey = `${item.id}_${idx}_${submenu.id}_${idy}`;
      const ancestors = [parentKey];
      consider(submenu, subKey, ancestors);

      submenu.children?.forEach((sub, idk) => {
        const childKey = `${item.id}_${idx}_${idy}_child_${idk}_${sub.id}`;
        consider(sub, childKey, [parentKey, subKey]);
      });
    });
  });

  return { selectedKey, openKeys: [...openKeys] };
}

const Sidebar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useSelector((state) => state.auth, shallowEqual);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { system_refund, payment_type } = useSelector(
    (state) => state.globalSettings.settings,
    shallowEqual,
  );
  const { navCollapsed } = useSelector(
    (state) => state.theme.theme,
    shallowEqual,
  );
  const dispatch = useDispatch();
  const { theme } = useSelector((state) => state.theme, shallowEqual);
  const parcelMode = useMemo(
    () => !!theme.parcelMode && user?.role === 'admin',
    [theme, user],
  );
  const { filterMenu } = useAdminPermissions();
  const routes = useMemo(() => {
    let list = user.urls;
    if (payment_type === 'admin') {
      list = list?.filter((item) => item?.name !== 'payments');
    }
    if (system_refund === '0') {
      list = list?.filter((item) => item?.name !== 'refunds');
    }
    /* Last, so a role's permissions decide what survives the other two rather
       than the reverse. A menu full of sections that answer 403 is not a menu.
       The server refuses the same set by route prefix — this is the courtesy
       half of that, not the enforcement. */
    return filterMenu(list);
  }, [user, payment_type, system_refund, filterMenu]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openKeys, setOpenKeys] = useState([]);
  const [data, setData] = useState(parcelMode ? allRoutes.parcel : routes);

  useDidUpdate(() => {
    if (parcelMode) {
      setData(allRoutes.parcel);
    } else {
      setData(routes);
    }
  }, [theme, user]);

  const addNewItem = (item) => {
    if (typeof item.url === 'undefined') return;
    if (item.name === 'logout') {
      setIsModalVisible(true);
      return;
    }
    const data = {
      ...item,
      icon: undefined,
      children: undefined,
      refetch: true,
    };
    dispatch(setMenu(data));
    navigate(`/${item.url}`);
  };


  const menuTrigger = (event) => {
    event.stopPropagation();
    dispatch(navCollapseTrigger());
  };

  // Live unread-count feed for sidebar badges. `NotificationBar` polls
  // /admin/notifications/unread-count and dispatches a
  // `ks:admin-notification-counts` event whenever the numbers change;
  // this component subscribes so items like "Trade-in → Requests" can
  // render their own per-type badge without a second HTTP call.
  const [notifCounts, setNotifCounts] = useState(() =>
    typeof window !== 'undefined' && window.__ksAdminNotificationCounts
      ? window.__ksAdminNotificationCounts
      : { total: 0, perType: {} },
  );
  useEffect(() => {
    const handler = (e) => setNotifCounts(e?.detail ?? { total: 0, perType: {} });
    window.addEventListener('ks:admin-notification-counts', handler);
    return () => window.removeEventListener('ks:admin-notification-counts', handler);
  }, []);
  // Map sidebar URLs → notification type keys. New entries plug in
  // here as we start firing new event types from the backend.
  const NOTIFICATION_BADGE_BY_URL = {
    'trade-in/requests': 'trade-in.request.created',
    orders: 'order.created',
    'repair/bookings': 'repair.booking.created',
    'reviews/product': 'review.created',
    refunds: 'order.refund.requested',
    subscriber: 'subscriber.created',
  };

  /**
   * Chat threads waiting on a reply, straight from Firestore.
   *
   * Not a notification type like the others: chats never touch the backend
   * database, so there is nothing for the unread-count endpoint to count.
   * AdminChatWatch subscribes once and publishes on the same window-event
   * pattern the notification counts use.
   */
  const [chatCounts, setChatCounts] = useState(() =>
    typeof window !== 'undefined' && window.__ksAdminChatCounts
      ? window.__ksAdminChatCounts
      : { total: 0, waiting: 0 },
  );
  useEffect(() => {
    const handler = (e) => setChatCounts(e?.detail ?? { total: 0, waiting: 0 });
    window.addEventListener(ADMIN_CHAT_COUNTS_EVENT, handler);
    return () => window.removeEventListener(ADMIN_CHAT_COUNTS_EVENT, handler);
  }, []);

  const badgeForItem = (url) => {
    const clean = String(url).replace(/^[/]+/, '');
    // `messages` and `chat` are two sidebar entries onto the same inbox —
    // both render AdminChatPanel — so a thread waiting for a reply has to
    // show on both, or whichever one the admin happens to use looks quiet.
    if (clean === 'messages' || clean === 'chat') return chatCounts?.waiting || 0;
    const type = NOTIFICATION_BADGE_BY_URL[clean];
    return type ? notifCounts?.perType?.[type] || 0 : 0;
  };

  /**
   * A parent carries the sum of its children.
   *
   * The counts that matter live on leaves — "Orders", "Requests", "Bookings" —
   * and those sit inside collapsed groups. Without this, a new order is
   * invisible until somebody happens to expand Order, which is the opposite of
   * what a badge is for.
   */
  const badgeForGroup = (item) => {
    const own = item?.url ? badgeForItem(item.url) : 0;
    const fromChildren = (item?.submenu ?? item?.children ?? []).reduce(
      (sum, child) => sum + badgeForGroup(child),
      0
    );
    return own + fromChildren;
  };
  const withBadge = (label, url) => {
    const count = badgeForItem(url);
    if (!count) return label;
    return (
      <Space size={8} align='center'>
        <span>{label}</span>
        <Badge count={count} overflowCount={99} size='small' />
      </Space>
    );
  };

  /**
   * The same badge on a group header, summing what is inside it.
   *
   * Rendered in the group's own row rather than pushed to the right, because
   * the sidebar collapses to icons and a right-aligned number would be the
   * first thing clipped.
   */
  const withGroupBadge = (label, item) => {
    const count = badgeForGroup(item);
    if (!count) return label;
    return (
      <Space size={8} align='center'>
        <span>{label}</span>
        <Badge count={count} overflowCount={99} size='small' />
      </Space>
    );
  };

  const addMenuItem = (payload) => {
    const data = { ...payload, icon: undefined };
    dispatch(addMenu(data));
  };

  const handleOk = () => {
    batch(() => {
      dispatch(clearUser());
      dispatch(clearMenu());
      dispatch(setCurrentChat(null));
      dispatch(clearBags());
    });
    setIsModalVisible(false);
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleCancel = () => setIsModalVisible(false);

  function getOptionList(routes) {
    const optionTree = [];
    // forEach, not map: these only push into optionTree, so map was building
    // and discarding three arrays on every call.
    routes?.forEach((item) => {
      optionTree.push(item);
      item?.submenu?.forEach((sub) => {
        optionTree.push(sub);
        sub?.children?.forEach((child) => {
          optionTree.push(child);
        });
      });
    });
    return optionTree;
  }

  const optionList = getOptionList(data);

  const menuList =
    searchTerm.length > 0
      ? optionList.filter((input) =>
          t(input?.name ?? '')
            .toUpperCase()
            .includes(searchTerm.toUpperCase()),
        )
      : data;

  const menuPath = normalizeMenuPath(pathname);

  const { menuItems, selectedKey, requiredOpenKeys } = useMemo(() => {
    const selection = findMenuSelection(data, menuPath);
    if (!menuList?.length) {
      return { menuItems: [], selectedKey: selection.selectedKey, requiredOpenKeys: selection.openKeys };
    }
    const items = menuList.map((item, idx) => {
      if (item.submenu?.length > 0) {
        const children = item.submenu.map((submenu, idy) => {
          const subKey = `${item.id}_${idx}_${submenu.id}_${idy}`;
          return submenu.children?.length > 0
            ? {
                key: subKey,
                icon: getSystemIcons(submenu.icon),
                label: withGroupBadge(t(submenu.name), submenu),
                children: submenu.children.map((sub, idk) => {
                  const childKey = `${item.id}_${idx}_${idy}_child_${idk}_${sub.id}`;
                  return {
                    key: childKey,
                    icon: getSystemIcons(sub.icon),
                    label: (
                      <Link
                        to={'/' + sub.url}
                        onClick={() => addMenuItem(sub)}
                      >
                        {withBadge(t(sub.name), sub.url)}
                      </Link>
                    ),
                  };
                }),
              }
            : {
                key: subKey,
                icon: getSystemIcons(submenu.icon),
                label: (
                  <Link
                    to={'/' + submenu.url}
                    onClick={() => addNewItem(submenu)}
                  >
                    {withBadge(t(submenu.name), submenu.url)}
                  </Link>
                ),
              };
        });
        const parentKey = `${item.id}_${idx}`;
        return {
          key: parentKey,
          icon: getSystemIcons(item.icon),
          label: withGroupBadge(t(item.name), item),
          children,
        };
      }
      if (item.name === 'logout') {
        const key = `${item.id}_${idx}`;
        return {
          key,
          icon: getSystemIcons(item.icon),
          label: (
            <span
              className='sidebar-menu-logout'
              role='button'
              tabIndex={0}
              onClick={() => setIsModalVisible(true)}
              onKeyDown={(e) => e.key === 'Enter' && setIsModalVisible(true)}
            >
              {t(item.name)}
            </span>
          ),
        };
      }
      const key = `${item.id}_${idx}`;
      return {
        key,
        icon: getSystemIcons(item.icon),
        label: (
          <Link to={'/' + item.url} onClick={() => addNewItem(item)}>
            {t(item.name)}
          </Link>
        ),
      };
    });

    // Arrange the built items into labelled zones. Grouping happens after the
    // items are built, never before: the menu keys embed each entry's index in
    // `data`, and `findMenuSelection` derives the selected key from those same
    // indices — reordering the source list first would break highlighting.
    // While searching, results stay flat, since a filtered list has no zones.
    const isSearching = searchTerm.length > 0;
    let grouped = items;
    if (!isSearching) {
      const byId = new Map(menuList.map((item, idx) => [item.id, items[idx]]));
      const take = (entries) =>
        entries.map((entry) => byId.get(entry.id)).filter(Boolean);
      const { top, sections, ungrouped, bottom } = groupMenuBySection(menuList);
      grouped = [
        ...take(top),
        ...sections.map((section) => ({
          key: `section_${section.key}`,
          type: 'group',
          label: t(section.labelKey),
          children: take(section.items),
        })),
        ...take(ungrouped),
        ...take(bottom),
      ];
    }

    return {
      menuItems: grouped,
      selectedKey: selection.selectedKey,
      requiredOpenKeys: selection.openKeys,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuList, data, menuPath, t, i18n.language, notifCounts, searchTerm]);

  useEffect(() => {
    if (!selectedKey) return;
    setOpenKeys((prev) => [
      ...new Set([...prev, ...requiredOpenKeys]),
    ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependencies are deliberate; the missing ones are redefined each render
  }, [menuPath, selectedKey, requiredOpenKeys.join('|')]);

  return (
    <>
      <Sider
        className='navbar-nav side-nav'
        width={250}
        collapsed={navCollapsed}
        style={{ height: '100vh', top: 0, display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ flexShrink: 0 }}>
          <NavProfile user={user} />
          <div className='menu-collapse' onClick={menuTrigger}>
            <MenuFoldOutlined />
          </div>
          {navCollapsed && (
            <div className='flex justify-content-center'>
              <ThemeConfigurator />
            </div>
          )}

          {!navCollapsed ? (
            <Space className='mx-4 mt-2 d-flex justify-content-between' align='center'>
              <Select
                className='sidebar-lang-select'
                size='small'
                value={(i18n.language || 'en').split('-')[0]}
                options={LANGUAGE_OPTIONS}
                onChange={(lng) => {
                  i18n.changeLanguage(lng);
                  if (typeof localStorage !== 'undefined') {
                    localStorage.setItem('i18nextLng', lng);
                  }
                  dispatch(directionChange('ltr'));
                }}
                aria-label={t('language')}
                style={{ minWidth: 76 }}
              />
              <span className='d-flex'>
                <ThemeConfigurator />
                <NotificationBar />
                {/* Renders nothing; keeps the chat badge and the new-chat
                    chime alive from wherever in the dashboard you are. */}
                <AdminChatWatch />
                <AdminOrderWatch />
                {/* Mirrors the bell count into the browser tab title, so a new
                    order is visible from whichever tab the shop is actually in. */}
                <AdminTitleBadge />
              </span>
            </Space>
          ) : (
            <div className='menu-unfold' onClick={menuTrigger}>
              <MenuUnfoldOutlined />
            </div>
          )}
          <Divider style={{ margin: '10px 0' }} />

          {!navCollapsed && (
            <span className='mt-2 mb-2 d-flex justify-content-center'>
              <Input
                placeholder={t('search')}
                style={{ width: '90%' }}
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                }}
                prefix={<SearchOutlined />}
              />
            </span>
          )}
        </div>

        <Scrollbars
          className='sidebar-menu-wrap'
          autoHeight
          autoHeightMin={0}
          autoHeightMax='calc(100vh - 200px)'
          style={{ flex: '1 1 0', minHeight: 0 }}
          autoHide
        >
          <Menu
            className='sidebar-main-menu'
            theme='light'
            mode='inline'
            selectedKeys={selectedKey ? [selectedKey] : []}
            openKeys={!navCollapsed ? openKeys : []}
            onOpenChange={setOpenKeys}
            items={menuItems}
          />
        </Scrollbars>
      </Sider>

      <Modal
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        centered
      >
        <LogoutOutlined
          style={{ fontSize: '25px', color: '#08c' }}
          theme='primary'
        />
        <span className='ml-2'>{t('leave.site')}</span>
      </Modal>
    </>
  );
};
export default Sidebar;
